import { useState } from 'react';
import { Stack, Row, Text, Button, EmptyState } from '../ui';
import { PaymentStatusBadge } from '../ui/PaymentStatusBadge';
import { LogPaymentModal } from './LogPaymentModal';
import { ReconcileModal } from './ReconcileModal';
import clsx from 'clsx';

/** Mobile card for a single payment row */
function PaymentCard({ p, onLog, onReconcile }: { p: any; onLog: (p: any) => void; onReconcile: (p: any) => void }) {
  const amt = p.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="hairline rounded-sm p-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{p.dealName ?? p.dealId}</p>
          {p.description && <p className="text-xs text-muted truncate">{p.description}</p>}
        </div>
        <PaymentStatusBadge status={p.status} />
      </div>
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold tnum">{p.currency} {amt}</p>
          {p.dueAt && <p className="text-xs text-muted">{new Date(p.dueAt).toLocaleDateString()}</p>}
          {p.counterpartyName && <p className="text-xs text-muted">{p.counterpartyName}</p>}
        </div>
        <div className="flex gap-1">
          {p.status === 'expected' && (
            <Button variant="secondary" size="sm" onClick={() => onLog(p)}>Log</Button>
          )}
          {p.status === 'logged' && (
            <Button variant="secondary" size="sm" onClick={() => onReconcile(p)}>Reconcile</Button>
          )}
        </div>
      </div>
      {p.status === 'logged' && p.referenceNumber && (
        <p className="text-xs text-muted mt-1">ref: {p.referenceNumber}</p>
      )}
    </div>
  );
}

const STATUS_ORDER = ['expected', 'logged', 'reconciled', 'disputed', 'cancelled'];

export function PaymentsTable({ payments, onRefresh, compact }: { payments: any[]; onRefresh: () => void; compact?: boolean }) {
  const [logTarget, setLogTarget] = useState<any>(null);
  const [reconcileTarget, setReconcileTarget] = useState<any>(null);

  const grouped: Record<string, any[]> = {};
  for (const p of payments) {
    if (!grouped[p.status]) grouped[p.status] = [];
    grouped[p.status].push(p);
  }
  const groups = STATUS_ORDER.filter(s => grouped[s]?.length > 0);

  if (payments.length === 0) return <EmptyState title="no payments" hint="payments appear here once created or auto-generated on contract signed" />;

  return (
    <>
      <Stack gap={3}>
        {groups.map(status => (
          <div key={status}>
            <Text variant="micro" className="mb-2 uppercase tracking-wider">{status}</Text>

            {/* Mobile card view */}
            <div className="md:hidden space-y-2">
              {(grouped[status] ?? []).map((p: any) => (
                <PaymentCard key={p.id} p={p} onLog={setLogTarget} onReconcile={setReconcileTarget} />
              ))}
            </div>

            {/* Desktop table view */}
            <div className={clsx('hidden md:block hairline rounded-sm overflow-x-auto', compact ? 'text-xs' : 'text-sm')}>
              <table className="w-full min-w-[540px]">
                <thead><tr className="hairline-b bg-whisper">
                  <th className="text-left px-3 py-2 text-[11px] font-medium text-muted">Deal</th>
                  <th className="text-left px-3 py-2 text-[11px] font-medium text-muted">Counterparty</th>
                  <th className="text-right px-3 py-2 text-[11px] font-medium text-muted">Amount</th>
                  <th className="text-left px-3 py-2 text-[11px] font-medium text-muted hidden md:table-cell">Due</th>
                  <th className="text-left px-3 py-2 text-[11px] font-medium text-muted">Status</th>
                  <th className="px-3 py-2 text-[11px] font-medium text-muted text-right">Action</th>
                </tr></thead>
                <tbody>
                  {(grouped[status] ?? []).map((p: any, i: number) => (
                    <tr key={p.id} className={clsx('hairline-b last:border-b-0', i % 2 === 0 ? '' : 'bg-whisper/40')}>
                      <td className="px-3 py-2.5">
                        <Text variant="small" className="font-medium truncate max-w-[140px]">{p.dealName ?? p.dealId}</Text>
                        {p.description && <Text variant="label" muted className="truncate max-w-[140px]">{p.description}</Text>}
                      </td>
                      <td className="px-3 py-2.5"><Text variant="small" muted className="truncate max-w-[120px]">{p.counterpartyName ?? p.payeeDisplay ?? p.payerDisplay ?? '--'}</Text></td>
                      <td className="px-3 py-2.5 text-right tnum"><Text variant="small" className="font-medium">{p.currency} {p.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></td>
                      <td className="px-3 py-2.5 hidden md:table-cell"><Text variant="small" muted>{p.dueAt ? new Date(p.dueAt).toLocaleDateString() : '--'}</Text></td>
                      <td className="px-3 py-2.5">
                        <PaymentStatusBadge status={p.status} />
                        {p.status === 'logged' && p.referenceNumber && <Text variant="label" muted className="mt-0.5">ref: {p.referenceNumber}</Text>}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Row gap={1} justify="end">
                          {p.status === 'expected' && <Button variant="secondary" size="sm" onClick={() => setLogTarget(p)}>Log</Button>}
                          {p.status === 'logged' && <Button variant="secondary" size="sm" onClick={() => setReconcileTarget(p)}>Reconcile</Button>}
                        </Row>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </Stack>
      <LogPaymentModal payment={logTarget} open={!!logTarget} onClose={() => setLogTarget(null)} onSuccess={onRefresh} />
      <ReconcileModal payment={reconcileTarget} open={!!reconcileTarget} onClose={() => setReconcileTarget(null)} onSuccess={onRefresh} />
    </>
  );
}
